// الختمة: خطة لقراءة المصحف كاملاً في عدد من الأيام، ووِرد كل يوم منها.
//
// الخطة: { start: 'YYYY-MM-DD', ends: [آخر صفحة في كل يوم], progress, reminder }
// - ends تُحسب مرة عند البدء: توزيع متساوٍ على الصفحات، أو تحزيب الصحابة.
// - progress آخر صفحة قُرئت. تتقدّم بتقليب الصفحات بالترتيب لا بالقفز —
//   فتح سورة من الفهرس أو نتيجة بحث تصفّحٌ لا قراءة — وبزر «وصلت إلى هنا».

export const TOTAL_PAGES = 604;
const KEY = 'moslam_khatma';

/*
  تحزيب الصحابة رضي الله عنهم: سبعة أحزاب بالسور، آخر سورة في كل حزب.
  الفاتحة–النساء، المائدة–التوبة، يونس–النحل، الإسراء–الفرقان،
  الشعراء–يس، الصافات–الحجرات، ق–الناس.
*/
const SAHABA_LAST_SURAHS = [4, 9, 16, 25, 36, 49, 114];

export const KHATMA_PLANS = [
  { id: 'd3', days: 3, label: '٣ أيام' },
  { id: 'sahaba', days: 7, label: '٧ أيام', hint: 'تحزيب الصحابة بالسور' },
  { id: 'd10', days: 10, label: '١٠ أيام' },
  { id: 'd15', days: 15, label: '١٥ يوماً' },
  { id: 'd30', days: 30, label: '٣٠ يوماً', hint: 'جزء كل يوم' },
];

/** آخر صفحة في كل يوم، بتوزيع متساوٍ. */
export const evenEnds = (days) =>
  Array.from({ length: days }, (_, k) => Math.round(((k + 1) * TOTAL_PAGES) / days));

/** آخر صفحة في كل حزب من أحزاب الصحابة. */
export function sahabaEnds({ surahs, ayahs }) {
  return SAHABA_LAST_SURAHS.map((n) => {
    const s = surahs[n - 1];
    return ayahs[s.start + s.count - 1][1];
  });
}

/** 'YYYY-MM-DD' بتوقيت الجهاز — الختمة يوميّة على يوم القارئ لا على UTC. */
export function dayKey(date = new Date()) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

/** أيام بين يومين ('YYYY-MM-DD')؛ بالتاريخ لا بالساعات فلا يزيحها التوقيت الصيفي. */
export function daysBetween(from, to) {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = to.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000);
}

export function createKhatma(planId, quran, { today = dayKey(), reminder = null } = {}) {
  const plan = KHATMA_PLANS.find((p) => p.id === planId);
  const ends = planId === 'sahaba' ? sahabaEnds(quran) : evenEnds(plan.days);
  return { planId, start: today, ends, progress: 0, reminder };
}

/**
 * حال الختمة اليوم.
 * @returns {{ day, days, from, to, left, behind, done, percent }}
 *   day: رقم اليوم (من ١)، وقد يتجاوز عدد الأيام إن تأخّر القارئ
 *   from–to: وِرد اليوم؛ left: ما بقي منه؛ behind: أيام التأخّر
 */
export function khatmaStatus(k, today = dayKey()) {
  const days = k.ends.length;
  const day = Math.max(0, daysBetween(k.start, today)) + 1;
  const done = k.progress >= TOTAL_PAGES;
  const target = k.ends[Math.min(day, days) - 1];
  // اليوم الذي فيه القارئ فعلاً: أول يوم لم يبلغ آخرَ صفحاته
  const readingDay = k.ends.findIndex((end) => end > k.progress) + 1 || days;
  return {
    day,
    days,
    from: Math.min(TOTAL_PAGES, k.progress + 1),
    to: target,
    left: Math.max(0, target - k.progress),
    behind: done ? 0 : Math.max(0, Math.min(day, days) - readingDay),
    done,
    percent: Math.round((k.progress / TOTAL_PAGES) * 100),
  };
}

/** صفحة جديدة على الشاشة: تتقدّم الختمة إن كانت التالية لما قُرئ (أو بعدها بواحدة). */
export function advanceOnTurn(k, page) {
  if (page > k.progress && page <= k.progress + 2) return { ...k, progress: page };
  return k;
}

export const markProgress = (k, page) => ({ ...k, progress: Math.min(TOTAL_PAGES, Math.max(0, page)) });

export function readKhatma() {
  try {
    const k = JSON.parse(localStorage.getItem(KEY) || 'null');
    return k && Array.isArray(k.ends) && typeof k.start === 'string' ? k : null;
  } catch {
    return null;
  }
}

export function writeKhatma(k) {
  try {
    if (k) localStorage.setItem(KEY, JSON.stringify(k));
    else localStorage.removeItem(KEY);
  } catch {
    // التخزين ممنوع — الختمة لهذه الجلسة
  }
}
